/**
 * Tuning — OWNED BY KIMI. Every gameplay-feel constant lives here.
 * Subagents consume; they never add constants inline.
 * Numbers encode GAME_DESIGN.md v0.1 (locked decisions).
 */
export const TUNING = {
  sim: {
    dt: 1 / 60,
  },

  road: {
    /**
     * Lane width (m) — ARCADE, not physical. A real highway lane is 3.5-3.7 m;
     * this road is deliberately built to Temple Run / Subway Surfers
     * proportions, where a lane is exactly one vehicle wide and dodging is a
     * commitment rather than a nudge. At 2.7 m a semi (2.6 m wide) FILLS its
     * lane and two adjacent trucks nearly touch — the intended drama. The
     * spawner's guaranteed path always keeps one lane free of traffic at every
     * instant (spawner.ts solvability invariant), so "tight" never means
     * "unfair".
     */
    laneWidth: 2.7, // m
    laneCount: 3,
    /**
     * Shoulder (m each side) — deliberately a HAIR, not a lane.
     *
     * The road must read as exactly 3 vehicles wide (Temple Run / Subway
     * Surfers tightness), with the guardrails hard against the outer lanes.
     * 0.25 m is just enough margin that an outer-lane truck does not visually
     * kiss the rail: the widest model (semi, 2.6 m) centred in the outer lane
     * at |x| = 2.7 reaches 4.0 m, the painted lane edge is at 4.05 m, and the
     * rail box only starts at corridorHalfWidth = 4.3 m.
     */
    shoulderWidth: 0.25, // m each side
    corridorHalfWidth: 3 * 2.7 / 2 + 0.25, // 4.3 m — road surface + shoulders
    /**
     * Lateral strafe clamp (m) — DERIVED, never hand-tuned.
     *
     * Rule: the player may ride every metre of asphalt between the guardrails.
     * The visible road surface runs out to corridorHalfWidth (4.3 m), which is
     * exactly where the guardrail box starts (render/world.ts: rails occupy
     * |x| 4.3..4.6), so the clamp is
     *     corridorHalfWidth − player.capsuleRadius (the visible bike half-width)
     *   = 4.3 − 0.45 = 3.85 m
     * i.e. the bike's outer edge just touches the asphalt edge. Written out
     * longhand below so the derivation (lanes + shoulders − bike half-width) is
     * the single source of truth; a previous "painted lane line" clamp left a
     * visibly rideable strip of road unreachable.
     *
     * ANTI-CAMP: a player parked here is out of reach of an outer-lane CAR
     * (2.7 m lane centre + the widest car contact half-width, suv 1.125 m,
     * = 3.825 m — 2.5 cm short) but NOT of an outer-lane TRUCK (boxtruck 1.4 ->
     * 4.10 m, semi 1.45 -> 4.15 m, both past this clamp). Every vehicle spawns
     * at an exact lane centre, so the shoulder is kept lethal by the spawner
     * instead: the guaranteed path never rests in one lane for more than
     * flow.pathTransitionMax, the stream fills every lane it is not in, and
     * flow.outerTruckInterval arms a TRUCK upgrade for each outer lane every
     * 12 s (spawner.ts ANTI-CAMP; the upgrade still has to find a legal instant,
     * so the measured worst interval between outer-lane trucks is ~22 s).
     */
    strafeHalfWidth: 3 * 2.7 / 2 + 0.25 - 0.45,
    chunkLength: 50, // m of road per recycled chunk
    /**
     * Road (and biome-content) chunks kept AHEAD of the player.
     *
     * RENDER-ONLY: the sim never reads this — the road ribbon (render/world.ts)
     * and the biome content streamer (render/biomes.ts) do. 6 → 8 (2026-08-01
     * draw-distance pass): the per-biome fog far went to ~200-210 m and the
     * content window follows `fog.far + 45`, so 300 m of road ran out before
     * the fog did and the ribbon ended in mid-air over the ground plane. 8
     * chunks = 400 m ahead, which clears the deepest fog (bridge, 210 + 45) with
     * a chunk to spare.
     */
    chunksAhead: 8,
    chunksBehind: 2,
    guardrailHeight: 0.9,
  },

  player: {
    // SPEED IS THE DIFFICULTY LEVER (owner decision): the whole ramp was lifted
    // +30% rather than making traffic denser. Density is pinned flat (see
    // traffic.densityStart) and the run gets harder purely because everything
    // closes faster — the flow spawner schedules in TIME (one block per path
    // transition),
    // so the arrival cadence in SECONDS barely moves while the metres (and the
    // reading time per metre) shrink.
    // DIFFICULTY PASS 2 (owner playtest: "bump difficulty" — same lever again).
    // The ramp is lifted a further ~10-13% at both ends AND shortened: the run
    // now opens at 17.2 m/s instead of 15.6 and tops out at 44 instead of 39,
    // reaching max in 200 s instead of 240. Nothing else moved — density is
    // still flat (traffic.densityStart) and every guard window in the spawner
    // is expressed in TIME, so the whole solvability set recomputes itself at
    // the new speeds (the sweeps in spawner.test.ts are the proof).
    speedStart: 17.2, // m/s (~62 km/h)
    speedMax: 44, // m/s (~158 km/h) — arcade power fantasy
    speedRampDuration: 200, // s to reach max
    lateralMaxSpeed: 12, // m/s — crosses a lane in ~0.3 s
    lateralAccel: 60, // m/s^2 while A/D held
    lateralDamping: 14, // 1/s exponential damp when no input (bike inertia)
    capsuleRadius: 0.45,
    capsuleHalfHeight: 0.8, // half-length of capsule cylinder segment
    cameraHeight: 1.35, // m above ground — road-bike rider eye height
    cameraPitchDeg: 7, // downward tilt — portrait frames carry more road, less sky
    // Portrait 9:16: vertical FOV must be high so the ~37° horizontal FOV of a
    // landscape 62° lens doesn't hide far-lane vehicles. 78° => ~49° hFOV.
    fovBase: 78,
    fovSpeedBonus: 6, // +6 deg at speedMax => ~54° hFOV
    strafeRollDeg: 3, // camera roll into strafe at full lateral speed
  },

  traffic: {
    // Oncoming traffic speed (v_veh) ALSO ramps (dual-speed difficulty). Raised
    // proportionally with the player ramp on every difficulty pass (see
    // player.speedStart); the latest one is +10-13%, which puts closing speeds
    // at 37.2 -> 76.0 m/s for cars and 32.9 -> 68.9 m/s for trucks (was
    // 33.8 -> 67.6 and 29.9 -> 61.1).
    carSpeedStart: 20,
    carSpeedMax: 32,
    truckSpeedStart: 15.7,
    truckSpeedMax: 24.9,
    carLength: 4.6,
    carWidth: 1.9,
    carHeight: 1.7, // collider covers the tallest car variant (suv 1.85 visual is close)
    truckLength: 9.0,
    truckWidth: 2.5,
    truckHeight: 3.6, // covers boxtruck 3.2 / semi 4.0 after proportion rescale
    // s — spawn distance = closingSpeed * this, minimum (the fog horizon below
    // is larger at every speed, so this floor is currently inert). The player's
    // actual reaction budget is now flow.crossMargin, which is a TIME window
    // reserved around every path transition rather than a spawn distance.
    minReactionTime: 0.75,
    // m — vehicles must spawn beyond the DRAW distance so they emerge gradually
    // instead of popping in. 160 was sized against the old ~140 m fog horizon;
    // the render pass extending the visible road to ~200-210 m makes anything
    // below ~220 a pop-in. 240 is the coordinated value: it clears the extended
    // draw distance with ~30 m of margin at every point on the speed ramp.
    // It also becomes the BINDING term in the spawn envelope at every speed
    // (spawner.ts (7)): closing * minReactionTime is 24.7 m at run start and
    // 51.7 m at full ramp, both far below 240.
    spawnHorizonMin: 240,
    // OPENING ARRIVAL (per run / per reset): the earliest arrival time any
    // vehicle may be scheduled for, and the exact arrival of the run's first
    // forced vehicle — which lands in lane 1, where the player starts. The
    // whole opening pipeline (every arrival out to the spawn horizon, ~7.3 s of
    // closing time at start speeds) is emitted on frame one onto an empty
    // screen, so the road is already a stream from the first frame and nothing
    // pops in. 2.0 s — the run opens ON a dodge; there is no "beat to read the
    // road" any more, by design. The guaranteed path leaves lane 1 before this
    // arrival (spawner.ts), so the opening is survivable by construction.
    firstGroupLeadTime: 2.0, // s of closing time until it reaches the player
                             // => z ≈ -74 m for a car at start speeds
    despawnBehind: 15, // m past player
    // Density: the volume lever. Consumed by the spawner as a scale on
    // TUNING.flow.blockFullChance and TUNING.flow.followerChance (both are
    // multiplied by min(1, density / densityStart)), so lowering it thins the
    // stream. It SATURATES upward: at densityStart the schedule is already one
    // block per path transition with the maximum follower rate, and the
    // physical same-lane headway is the floor nothing can cross.
    // High volume by design (Thomas: always >5 vehicles visible).
    //
    // FLAT DENSITY (owner decision): start == max, so the lever is a CONSTANT
    // and the block/follower rates are unscaled for the whole run.
    // Difficulty rises purely through the SPEED ramp — the same arrival cadence
    // (~0.33 s per vehicle somewhere on the road, after the density pass in
    // TUNING.flow) covers 2.5x more road, and a lane change costs 2.5x more of
    // the approach, by the end of the ramp.
    densityStart: 4.5,
    densityMax: 4.5,
    truckRatioStart: 0.15,
    truckRatioMax: 0.4,
    // Ambient (scenic) traffic outside guardrails, both directions.
    ambientLaneOffset: 2.5, // m beyond guardrail to first ambient lane
    ambientLaneCount: 2,
    ambientSpeedMin: 20, // m/s absolute
    ambientSpeedMax: 32,
    /**
     * DISABLED (0). Roadside ambients read as on-corridor threats in the
     * portrait FOV — an oncoming ambient at x = -9.25 sits dead-centre in the
     * frame at 40-120 m, so players dodge it and try to kick it — and they clip
     * through the city biome's buildings (which start at x ≈ 8). Re-enable only
     * with a visually separated roadway (its own elevation / offset / occluder)
     * that can never be mistaken for the playable corridor.
     */
    ambientDensity: 0, // per 100 m per direction (was 1.5)
    /**
     * Ambients are recycled once they are this far AHEAD of the player (m).
     * Same-direction scenic traffic drives AWAY from the player, so anything
     * beyond the fog horizon (~140-160 m) is invisible; recycling there keeps
     * the 16-slot ambient segment cycling instead of pinning every slot on a
     * car hundreds of metres ahead.
     */
    ambientDespawnAhead: 200,
    /**
     * Threat pool size — DERIVED FROM THE SPAWN HORIZON, not a taste value.
     *
     * A vehicle occupies a slot from spawn (spawnHorizonMin ahead) until
     * despawnBehind past the player, i.e. for
     * (spawnHorizonMin + despawnBehind) / closingSpeed seconds, and the stream
     * delivers one vehicle somewhere on the road every ~0.33 s. The binding
     * case is RUN START, where the horizon is widest in time (240 m at a
     * 32.9 m/s truck closing speed = 7.3 s of road).
     *
     * MEASURED worst simultaneous live threats over 120 seeds x 120 s
     * (ramping + max difficulty): 30, at t ≈ 2.2 s — the opening burst, where
     * the whole horizon is emitted onto an empty road at the slowest closing
     * speeds of the run. 24 was sized for the old 160 m horizon and silently
     * DROPPED spawn commands at 240 m: `spawnVehicle` returns null when the
     * pool is full, which quietly deleted block AND KICK WALL vehicles (the
     * wall sweeps in spawner.test.ts caught it as a wall lane that never
     * contacted a stationary bot). 40 leaves ~33% headroom over the measured
     * worst case. Raise it with traffic.spawnHorizonMin, always.
     */
    maxThreatVehicles: 40, // pool size (was 24, at a 160 m horizon)
    /**
     * Ambient pool size — 0 because ambient traffic is disabled (see
     * `ambientDensity`): roadside ambients read as on-corridor threats in the
     * portrait FOV and clip the city biome's buildings. All ambient code paths
     * (world.ts AmbientSpawner, vehicles.ts ambient branch) remain intact and
     * compiling; with density 0 and 0 pool slots they are inert. Re-enable only
     * together with a visually separated roadway.
     */
    maxAmbientVehicles: 0, // pool size (was 16)
  },

  /**
   * Per-MODEL real-world dimensions (m). One entry per vehicle GLB. The render
   * scales each GLB so its final world bbox equals these numbers exactly, and
   * the SIM builds its Rapier colliders + crash/near-miss extents from the same
   * table — so the hitbox always matches the vehicle you can actually see.
   *
   * Previously the sim used per-KIND constants (car 1.9 x 4.6, truck 2.5 x 9.0)
   * while the render drew five differently-sized models, so e.g. a hatchback
   * (1.75 wide, 4.0 long) carried a collider 0.15 m too wide and 0.6 m too
   * long, and a semi (11 m) carried one 2 m too SHORT. Model choice now lives
   * in the sim (vehicles.ts pickVehicleModel) and is exposed on the entity.
   */
  vehicleModels: {
    sedan: { length: 4.6, height: 1.46, width: 1.85 },
    suv: { length: 4.8, height: 1.9, width: 1.95 },
    hatchback: { length: 4.0, height: 1.5, width: 1.75 },
    boxtruck: { length: 7.0, height: 3.2, width: 2.5 },
    semi: { length: 11.0, height: 4.0, width: 2.6 },
  },

  /**
   * Player↔vehicle contact extents. These describe the VISIBLE rider, not the
   * physics capsule: a crash must fire when the vehicle overlaps what the
   * player can see, never before.
   *
   * The first-person camera sits at z = 0 but the bike is drawn in FRONT of it
   * — the procedural front wheel is centred at cameraRig WHEEL_Z with a 0.30 m
   * radius, and the handlebars span ~0.44 m at model scale. The
   * old test treated the player as a POINT at z = 0 with the 0.9 m physics
   * capsule diameter as its width, which fired crashes with up to 0.43 m of
   * visible daylight still showing beside the bike.
   */
  contact: {
    /**
     * Leading edge of the player's contact/kick volume, sim z.
     *
     * DELIBERATELY UNCHANGED at -1.45 by the 2026-08-02 cockpit pass, which
     * pulled the DRAWN front wheel back to WHEEL_Z = -0.85 (tip at -1.175) so
     * only its crown shows. This plane is not just the tyre: it is also the near
     * edge of the kick reach (sim/kick.ts KICK_NEAR_EDGE_Z) and the far edge of
     * the spawner's usable gap (sim/spawner.ts), and moving it would retune the
     * kick window and the crash timing — a gameplay change, not an art one.
     * Keeping it 0.28 m ahead of the visible tyre stays on the SAFE side (the
     * crash fires with the vehicle no closer than the bike, never later).
     */
    playerFrontZ: -1.45,
    /** Trailing edge of the rider behind the camera, sim z. */
    playerBackZ: 0.45,
    /** Visible half-width of bike + rider (handlebars / shoulders), m. */
    playerHalfWidth: 0.3,
    /**
     * Arcade forgiveness: the lateral hitbox is this much NARROWER than the
     * summed visual half-widths. Colliders are biased SMALLER than visuals so a
     * pass that looks clean always is; they are never biased larger.
     */
    forgiveness: 0.15,
  },

  kick: {
    /**
     * Cooldown after a CONNECTING kick (s).
     *
     * THE KICK IS A SCARCE RESOURCE (playtest decision). At 0.6 s the kick was
     * a second dodge button — always available, so the correct play was to mash
     * it at every approach. Five seconds means roughly one kick per two or three
     * traffic groups: the player has to choose WHICH vehicle is worth it, and a
     * kick reads as an event rather than a traversal verb.
     */
    cooldown: 5.0,
    /**
     * Cooldown after a WHIFF (s) — DELIBERATELY IDENTICAL to `cooldown`.
     *
     * Risk/reward: a miss costs exactly what a hit costs, so pressing early "to
     * be safe" is a real decision with a real price instead of a free probe.
     * Kept as its own constant (rather than deleted) because world.ts and the
     * kick-input tests reference the whiff path explicitly; set the two apart
     * again if the whiff ever needs its own price.
     */
    whiffCooldown: 5.0,
    windowAhead: 3.0, // m — FLOOR of the kick window (vehicle front within this)
    /**
     * Time-based kick window (s of closing time). The effective window is
     *   max(windowAhead, closingSpeed * windowAheadTime)
     * so the window stays a constant number of FRAMES rather than a constant
     * number of metres. At the 4 m fixed window the usable band (crashes fire
     * once the front face reaches contact.playerFrontZ = -1.45 m) was ~2.5 m —
     * about 0.1 s at closing speeds of 26–52 m/s, i.e. humanly impossible.
     *
     * 0.35 s put the connect band ~9 m out at run start and ~18 m at full ramp
     * — half the visible road — so a kick fired while the vehicle was still a
     * distant shape and read as "too early relative to the vehicle". 0.22 s
     * brought it to ~7.4 m / ~15 m, and the playtest verdict was still "I kick
     * air and the vehicle magically crashes".
     *
     * 0.12 s (owner playtest) puts the FAR edge at 4.5 m at run start and 9.1 m
     * at full ramp; the crash band starts at contact.playerFrontZ (-1.45 m), so
     * the usable band is 3.0 m / 7.7 m — roughly one to two bike lengths, i.e.
     * the foot meets the bumper. In TIME that band is 0.081 s at run start and
     * 0.101 s at full ramp (~5-6 frames at 60 Hz): the kick is now a timing
     * read, which is the intent. `windowAhead` (3 m) remains the metre floor
     * and is inert at every closing speed in the game (the slowest, a
     * start-speed truck, is 32.9 m/s -> 3.9 m).
     *
     * DERIVED CONSUMER: spawner.ts commitWall sizes a kick wall's back margin
     * from max(windowAheadTime, windowAhead / truckClosing) + wallSettleTime,
     * so the wall placement follows this constant automatically.
     */
    windowAheadTime: 0.12,
    /**
     * Lateral kick reach (m) — surface gap from a foot to the vehicle's near
     * side (the check is |dx| <= reachLateral + vehicleHalfWidth).
     *
     * A foot must reach a vehicle in the ADJACENT lane: at the arcade lane
     * pitch (2.7 m) the surface gap to an adjacent-lane car is
     * ≈ 2.7 - halfWidth ≈ 1.8 m, so 2.8 m clears it comfortably (it was sized
     * for the old 3.5 m pitch, where the gap was ≈ 2.55 m). It still does NOT
     * reach the far lane (5.4 m pitch − halfWidth ≈ 4.5 m). At the old 1.5 m
     * only SAME-lane vehicles were kickable, which read in playtest as "kicks
     * don't reach".
     */
    reachLateral: 2.8, // m (was 1.5)
    /**
     * How long before a KICK WALL's window opens the guaranteed path must have
     * been HOLDING the wall's lane (s) — the settle budget in the wall
     * placement's back-margin (spawner.ts commitWall).
     *
     * NOT the kinematic lane-cross time (2.7 / 12 = 0.225 s): the real player
     * crosses under lateralAccel/lateralDamping physics, overshoots the lane
     * centre by up to ~0.5 m and needs the damping to bleed that off before
     * they are stably parked over the centre — measured in-engine at ~0.65 s
     * from a forced transition to settled. A wall whose lane the path entered
     * only ~0.8 s earlier was legal under the old 0.225 s margin and humanly
     * unanswerable: the 0.22 s kick window opened while the player was still
     * wobbling. 0.6 s plus the overlap/kick-window terms puts the whole wall
     * at least ~1 s into a stable hold.
     */
    wallSettleTime: 0.6,
    /**
     * DOUBLE KICK — minimum distance (m) from the player's x to the NEAREST
     * lane centre before both legs may fire.
     *
     * The double kick is the reward for committing to a lane BOUNDARY: the
     * rider straddles two lanes and takes out one vehicle with each leg. At
     * 0.8 m of the 1.35 m half-lane the player must be visibly off-centre
     * (roughly the outer 40% of the lane on either side), so a normal
     * lane-centred kick can never accidentally spend two vehicles' worth of
     * one cooldown.
     */
    straddleMin: 0.8,
    /**
     * DOUBLE KICK — maximum |z| separation (m) between the two targets.
     *
     * Both vehicles must be alongside each other, not one trailing the other:
     * a single kick's worth of body rotation cannot plausibly reach two cars
     * spread further than about a car length apart, and a wider gate would let
     * a staggered kick wall (spawner.ts, deliberately offset in z) be cleared
     * with one press.
     */
    pairMaxDz: 2.5,
    // HEAVY launch: the impulse is mass-normalized (m/s deltas), so the only
    // way mass reads is through the numbers below. Owner playtest ("vehicles
    // feel too light/toy"): the wreck must leave INSTANTLY but SLOWER than it
    // arrived, fight the air on the way out, and slam back down. Everything
    // except the kick impulse itself is now on the realistic side of the dial —
    // see wreckAirDamping and gravityScale.
    launchBackSpeed: 13, // m/s reversed along its travel direction — the FLOOR
    /**
     * The kicked vehicle leaves at least this multiple of the speed it was
     * CLOSING at, floored by launchBackSpeed.
     *
     * Without this the launch was a fixed 26 m/s while the dual-speed director
     * ramps closing speed from ~26 m/s to ~52 m/s over a run: a vehicle that
     * arrived at 52 m/s left at 26, so the reversal ratio decayed from 1.00 at
     * the start of a run to 0.50 by the end and later kicks stopped reading as
     * launches at all. At 1.0 the car always departs at least as fast as it
     * arrived, which is what the early-run kicks (that felt right) were doing.
     *
     * Raised to 1.25 (from 1.0) on the reference-footage pass: a real kicked
     * vehicle does not merely reverse, it is FLUNG — it leaves faster than it
     * arrived and clears the corridor in a fraction of a second.
     *
     * HEAVY PASS (owner playtest: "the vehicle immediately flies backwards, but
     * SLOWER, with stronger resistance and gravity"): 1.25 -> 0.7. A two-ton
     * car cannot leave faster than it arrived off one boot — it now departs at
     * 70% of its closing speed (26 m/s at run start, 53 m/s at full ramp,
     * floored at launchBackSpeed) and then loses that speed visibly to
     * wreckAirDamping. The departure is still INSTANT: hitstop stays 0 and the
     * full velocity is set on the connect frame, so only the magnitude changed.
     *
     * MASS PASS (owner playtest 2026-08-02: "the vehicle crash backwards is a
     * bit too fast — dampen it to look heavier"): 0.7 -> 0.52, with the FLOOR
     * cut 20 -> 13 m/s so the floor does not silently undo the ratio at the slow
     * end of the ramp. Departure speeds are now ~17 m/s at run start (closing
     * 33 m/s) and ~27 m/s at a 52 m/s closing ramp — the wreck still leaves on
     * the connect frame and is clear of the corridor inside ~0.3 s, but it
     * leaves at HALF the speed it arrived at, which is what reads as two tons
     * rather than a hockey puck. Safety is unaffected either way: a kicked body
     * is in COLLISION_THREAT_KICKED (mask = STATIC only) for the rest of its
     * life, so it can never collide with the player however slowly it leaves.
     */
    launchBackClosingRatio: 0.52,
    // Deflection profile. The wreck veers to one side, but as a heavy body:
    // lateral and spin were cut with the back speed on the HEAVY pass so the
    // wreck no longer skates sideways like a puck. Vertical is deliberately
    // untouched (see gravityScale) — the pop off the road is the only thing
    // that keeps an airborne beat at all at 6.2 g.
    // MASS PASS: 14 -> 10. The sideways component is what made the wreck read
    // as skating; at 10 m/s it still leaves its lane but the departure is
    // dominated by the (also reduced) backward throw.
    launchLateralSpeed: 10, // m/s away from corridor (was 22, then 14)
    /**
     * SPIN-OUT PASS (owner playtest 2026-08-03, third rejection: "still too
     * light and acrobatic. Make it SUPER HEAVY (just that we have a super strong
     * kick) and realistic how a car/big truck will crash in real physics").
     *
     * 4.2 -> 1.4 (car) and 2.7 -> 0.9 (truck). This is the constant that decides
     * whether the wreck is a THROWN OBJECT or a SHOVED ONE, and every previous
     * pass only trimmed it. A massive body taking a huge horizontal impulse does
     * not leave the ground: the impulse is horizontal, the weight is enormous,
     * and the only vertical component is the moment of unweighting as the
     * suspension unloads. At 1.4 m/s against 5.8 g the apex is 1.7 CENTIMETRES
     * and the body is back down in 0.05 s — the wreck hops, it does not fly, and
     * the whole spectacle happens on the asphalt where the grinding is.
     *
     * The "super strong kick" now reads entirely through SPEED and YAW (see
     * launchBackClosingRatio and launchYaw*), which is what dashcam footage of a
     * real high-energy side impact actually looks like: the car is fired
     * backwards, spinning flat, not launched into the air.
     */
    launchUpCar: 1.4,
    launchUpTruck: 0.9,
    // --- LAUNCH SPIN: A FLAT SPIN-OUT, NOT A TUMBLE (owner, 2026-08-03) ------
    // "Still too light and acrobatic. Make it SUPER HEAVY (just that we have a
    // super strong kick) and realistic how a car/big truck will crash in real
    // physics."
    //
    // THE MODEL THIS REPLACES. Three passes tried to tune a multi-flip
    // friction-true TUMBLE down to "heavy" (rate bands, budgets, ceilings) and
    // the owner rejected all three. The defect was never the numbers: a body
    // that flips repeatedly is, by construction, acrobatic. Real physics of a
    // massive horizontal shove is different in KIND. The kinetic energy goes
    // into a flat YAW SPIN-OUT while the vehicle slides backward on its tyres
    // and underside, and the vertical axis barely moves; at the end of that
    // slide, as lateral grip builds against the direction of travel, the vehicle
    // trips over its own wheels and topples ONCE onto its side. Cars do this;
    // high-CG trucks and semis roll sideways for the same reason but slower, and
    // never end-over-end.
    //
    // So the launch spin is now dominated by YAW, with only a small tilt seed:
    //   - YAW carries the "super strong kick" read. Its SIGN is derived, not
    //     drawn: the kick lands on the vehicle's player-facing flank, i.e. an
    //     off-centre impulse at r = -sign(dx)·(w/4) x̂ against a mostly -Z force,
    //     giving tau_y = (r x F)_y of sign -sign(dx). A vehicle on the player's
    //     RIGHT therefore spins clockwise seen from above — its far flank swings
    //     back — which is what the contact geometry demands and what a viewer
    //     reads as "the kick caught its corner".
    //   - TILT is a SEED, not a flip: `launchTiltRate` about the friction-true
    //     axis normalize(up x v) (see kick.ts `frictionTumbleAxis`). At ~1.2
    //     rad/s a car would need 1.3 s to reach 90 degrees and it never gets it
    //     — friction and the ground kill it. The actual topple is DRIVEN, once,
    //     by the ground controller (world.ts `enforceWreckGround`), which is
    //     both what a real trip is (grip, not launch energy) and the only way to
    //     bound the rotation to one topple.
    /**
     * REFERENCE VEHICLE LENGTH (m) every spin rate is calibrated for — a sedan.
     * Ground rates are scaled by `min(1, tumbleRefLength / modelLength)`, so a
     * box truck (7 m) turns at 0.66x and a semi (11 m) at 0.42x of the car rate.
     * This is the tripping ceiling w ~ v / h: h — ground to centre of mass, the
     * lever the body rotates over — grows with the vehicle, so a long vehicle
     * physically topples SLOWER. Unscaled, the old enforcement reared semis onto
     * their ends and cartwheeled them with their centres 6 m in the air.
     */
    tumbleRefLength: 4.6,
    /**
     * Scale on the LAUNCH spin for `kind === "truck"`. The launch has only the
     * kind to go on (no model), and the truck models are 7 m and 11 m, so 0.5 is
     * the matching approximation to `tumbleRefLength / length`.
     */
    tumbleTruckScale: 0.5,
    /** +/-fraction of seeded jitter on the launch yaw rate (per kick). */
    tumbleJitter: 0.2,
    /**
     * LAUNCH YAW band (rad/s) — the flat spin-out the kick imparts.
     *
     * This is where the "impossibly strong kick" now lives. 5.5-8.5 rad/s is
     * 0.9-1.35 rev/s about the VERTICAL axis, which decays through the slide
     * against tyre scrub: measured total yaw over a wreck's life lands at
     * 180-540 degrees, the band that reads as a real spin-out in dashcam
     * footage. It is deliberately HIGHER than the old `tumbleYaw*` (3-5) it
     * replaces, because yaw is no longer competing with a flip for the rotation
     * budget — it IS the rotation.
     */
    launchYawMin: 5.5,
    launchYawMax: 8.5,
    /**
     * LAUNCH TILT seed (rad/s) about the friction-true axis — deliberately far
     * too small to flip the vehicle on its own (90 degrees would take 1.3 s of
     * unopposed rotation, and the wreck is on the road within 0.05 s). It exists
     * so the body is never perfectly level when the ground takes it, which is
     * what makes the driven topple look like a continuation rather than a
     * scripted event.
     */
    launchTiltRate: 1.2,
    /**
     * Gravity multiplier on a kicked body — the WEIGHT lever.
     *
     * 2.6 -> 5.0 on the heavy-impact pass (playtest: "kicked vehicles seem too
     * light — no heavy impact feel"). At 2.6 g the launch arc (launchUpCar 5.5)
     * peaked 0.6 m up and hung ~0.43 s; at 5.0 g (49 m/s²) it peaks ~0.31 m and
     * the wreck is back on the asphalt in ~0.22 s, so the fling reads as a
     * TWO-TON object being punted rather than a balloon on a parabola. The
     * landing thud (kickedVehicleGroundHit) therefore also fires while the wreck
     * is still travelling at nearly its full launch speed, which is what makes
     * the ground slide + smoke trail below the dominant beat of the effect.
     *
     * launchUpCar/launchUpTruck were deliberately NOT trimmed with it: at 5.0 g
     * the arc is already sub-quarter-second and cutting the up component further
     * removes the pop off the ground entirely — the wreck would simply slide,
     * with no airborne beat at all.
     *
     * HEAVY PASS: 5.0 -> 6.2 (61 m/s²). The car's arc (launchUpCar 5.5) peaks
     * 0.25 m up and is back on the asphalt in 0.18 s, so the wreck SLAMS down
     * rather than settling. Together with wreckAirDamping this is the whole
     * "realistic physics except the kick force itself" note: strong gravity,
     * strong drag, a modest impulse.
     *
     * MASS PASS 2 (2026-08-03): 6.2 -> 5.8, TOGETHER WITH launchUpCar 5.5 ->
     * 4.2. The float is what the owner was reacting to and the LAUNCH is where
     * it lives: at 4.2 m/s against 5.8 g the arc peaks 0.155 m and lasts 0.148 s
     * — flatter and shorter than the 0.25 m / 0.18 s the previous pair produced,
     * so this is a NET reduction in air time despite the softer gravity. Giving
     * that 0.4 g back is what buys the ground phase: gravity presses the wreck
     * into the road, and at 6.2 the (deliberately slowed) tumble was scrubbed
     * out in under 3 m — measured, gravityScale is by far the strongest lever on
     * the slide, 6.8 -> 5.8 moving the median from 2.8 m to 4.6 m and the share
     * of wrecks reaching 300° of ground rotation from 0.54 to 0.73. A wreck that
     * stops in three metres has no room to grind, and the grind is the whole
     * point of the pass.
     */
    gravityScale: 5.8,
    /**
     * Ground friction of a kicked wreck's collider — the STOPPING lever.
     *
     * Ground deceleration is friction · 9.81 · `gravityScale`, i.e. linear in
     * this number: 0.45 · 9.81 · 5.8 ≈ 25.6 m/s², so a wreck touching down at
     * 17 m/s sheds it in ~0.7 s and ~6 m of slide.
     *
     * SPIN-OUT PASS (2026-08-03): 0.18 -> 0.45. Every earlier pass was buying
     * LENGTH — the owner had asked for long visible skids and the model had a
     * multi-flip tumble to carry over them. The new model wants the opposite:
     * "SUPER HEAVY" means the wreck sheds its energy fast and violently, into a
     * spin-out and a topple, not into a long glide. Friction is also what
     * supplies the lateral GRIP the topple trips over — a low-friction wreck
     * slides flat and never catches. Measured slide (touchdown to rest): median
     * ~5-7 m, p90 well inside 20 m, which is the band this pass targets.
     */
    wreckFriction: 0.45,
    // --- GROUND PHASE: SPIN-OUT, THEN EXACTLY ONE TOPPLE --------------------
    // One controller owns a wreck from touchdown to rest (world.ts
    // `enforceWreckGround`). It replaces `enforceWreckTumble` +
    // `enforceWreckTip` and the whole rate-band / rotation-budget / nose-grind
    // apparatus those two had accreted over three tuning rounds.
    //
    // PHASE 1 - SPIN-OUT. The wreck slides backward while YAWING flat, tyres and
    // underside grinding (the scrape dust rides on this, unchanged). Yaw is
    // floored at a speed-coupled rate so the slew survives the whole slide; the
    // vertical axis is left alone entirely.
    // PHASE 2 - THE TOPPLE. After `toppleDelay` of grinding, lateral grip has
    // built against the slide and the vehicle trips: the controller drives ONE
    // slow rotation onto the side/roof and then retires for good. Cars pick
    // trip-vs-side-roll from the launch rng; trucks and semis ALWAYS roll
    // sideways about their long axis, because that is the only way an 11 m body
    // with a high centre of mass goes over.
    // PHASE 3 - SLAM AND SETTLE. Completion takes a hard velocity haircut and a
    // downward kick (which fires the existing slam/dust machinery), then the
    // angular damping goes up so the wreck CANNOT re-flip. Settle, blink out.
    /**
     * SPIN-OUT YAW — rad/s of yaw held per m/s of the wreck's horizontal ground
     * speed, clamped to `spinoutYawMin..Max`, in whichever direction the launch
     * already chose. Speed-coupled so it reads as scrub rather than as a motor:
     * violent while the wreck is fast, gone by the time it stops.
     */
    spinoutYawPerSpeed: 0.45,
    /** Floor (rad/s) on the held spin-out yaw. */
    spinoutYawMin: 3.0,
    /**
     * Cap (rad/s) on the held spin-out yaw — 1 rev/s. Above this a flat spin
     * stops reading as a car and starts reading as a fidget spinner.
     */
    spinoutYawMax: 6.5,
    /**
     * Ground speed (m/s) below which the spin-out yaw floor is released. Well
     * above the rest thresholds (world.ts KICK_REST_LIN 0.4), so the last part of
     * the slide and the whole settle path are unforced physics.
     */
    spinoutMinSpeed: 2.0,
    /**
     * Ground speed (m/s) below which the monotone speed RATCHET releases the
     * wreck for good (world.ts `enforceWreckGround`). The ratchet is what stops
     * any held rotation acting as a motor — Rapier turns an enforced spin on a
     * body in contact back into travel, exactly like a driven wheel — but it must
     * not still be clamping when the topple rolls a nearly-stopped wreck over,
     * because that roll MOVES the centre and a tight clamp pins the wreck flat.
     */
    groundReleaseSpeed: 1.2,
    /**
     * Seconds of grinding spin-out after touchdown before the topple begins.
     *
     * This is the beat that makes the crash read as physics rather than as an
     * animation: the wreck arrives, spins flat, grinds — and only then does the
     * grip catch and put it over. Too short and the topple happens mid-air-ish,
     * at the launch, which is the acrobatic read; too long and slow wrecks stop
     * before they trip. 0.35 s is ~21 frames of visible spin-out.
     */
    toppleDelay: 0.35,
    /**
     * PEAK rad/s of the driven topple for a reference-length car, scaled by
     * `min(1, tumbleRefLength / modelLength)` (a box truck 0.66x, a semi 0.42x).
     *
     * 3.6 rad/s puts a 90-degree topple at ~0.44 s and a 180-degree one at
     * ~0.87 s for a car, and 1.5 rad/s / ~1.0 s for the 90 degrees a semi needs:
     * slow enough that the body visibly LEANS, hangs on its wheel edge and then
     * goes over. The whole owner-facing point of the number is that a topple is a
     * one-second event, not a flip.
     */
    toppleRate: 3.6,
    /**
     * Fraction of the topple rate held during the first `toppleRampTime` seconds
     * of the topple, ramping linearly to full. A body does not acquire its roll
     * rate instantly; the ramp is what makes the lean read as grip building
     * rather than as a torque being switched on.
     */
    toppleRampTime: 0.25,
    /**
     * |up . worldUp| below which the topple is COMPLETE — the wreck is past its
     * balance point and on its side (0.4 ~ 66 degrees of lean). Paired with the
     * long-axis test (`tipLongDot`) so "complete" always means a genuine
     * side/roof pose, never a nose stand.
     */
    toppleDoneDot: 0.25,
    /**
     * HARD BUDGET (rad) of driven non-yaw rotation per wreck. 7.8 rad = 447
     * degrees; a topple needs 90-180. Nothing in this model may spend more, so
     * "at most one topple" is bounded by construction and not by tuning. Reaching
     * it retires the controller and damps the wreck out.
     */
    toppleMaxRot: 7.8,
    /**
     * Hard safety cap (s) on driven topple time per wreck. A topple resolves in
     * ~1 s; this only exists so a body wedged against a guardrail can never loop
     * forever or leak its pool slot.
     */
    toppleMaxTime: 2.5,
    /**
     * Fraction of HORIZONTAL speed kept when the topple lands — the SLAM. A car
     * that has just dropped onto its flank does not keep sliding at the speed it
     * was spinning at: the flat panel digs in and eats the energy in one frame.
     */
    toppleSlamKeep: 0.5,
    /**
     * Downward velocity (m/s) injected on the frame the topple completes, so the
     * existing per-impact slam detector (world.ts KICK_SLAM_ARM_VY) sees a
     * genuine fall and the landing gets its dust burst. Below the arm threshold
     * by design.
     */
    toppleSlamVy: -3.5,
    /**
     * Angular damping (1/s) applied for the rest of a wreck's life once the
     * topple is done (or the budget is spent). THIS is what makes "exactly one
     * topple" true rather than likely: at 6 the residual spin is gone inside
     * ~0.2 s, so a wreck that has landed on its side cannot roll on through onto
     * its roof and back.
     */
    toppleSpentDamping: 6,
    /**
     * HARD CEILING (rad/s) on a kicked wreck's angular speed once it is on the
     * ground. The controller's own rates are bounded, but CONTACTS are not: a
     * body that catches a corner or clips a rail picks up single-frame rates
     * nothing asked for, and those frames are exactly the acrobatic read. The
     * clamp rescales the angular velocity VECTOR, so the axis survives and only
     * the magnitude is shed. 6.5 rad/s ~ 1 rev/s, a hair above the largest rate
     * the controller ever holds (`spinoutYawMax`).
     */
    wreckMaxSpin: 6.5,
    /**
     * |up . worldUp| above which a settled pose counts as FLAT (wheels-down or
     * roof-down) and the rest test refuses it. The topple controller is what
     * delivers the guarantee; this refusal is the backstop that keeps the
     * guarantee honest while the topple is still in progress.
     */
    tipDot: 0.5,
    /**
     * Hard cap (s) on the flat-rest settle REFUSAL per wreck, so a pathological
     * body (perched on a rail, wedged on a prop) can never leak its pool slot.
     */
    tipMaxTime: 3,
    // --- THE LONG-AXIS CONSTRAINT (owner playtest 2026-08-03) ---------------
    // "Sometimes they land and slide on a SHORT side (like the front), which is
    // unrealistic."
    //
    // THE GEOMETRY HOLE. A rest guarantee that constrains only the body's UP
    // axis (`tipDot`) is blind to a car standing on its NOSE: up is HORIZONTAL
    // there, so the pose passes with full marks while balanced on a bumper. The
    // only physically stable rests for a car-shaped box are SIDE and ROOF, and
    // both have the LONG axis (local +Z) near-horizontal.
    //
    // KEPT THROUGH THE SPIN-OUT PIVOT. The topple controller makes nose stands
    // far rarer (a side-roll about the long axis cannot produce one at all, and
    // it is the only mode trucks get), but a car in TRIP mode still swings its
    // long axis through vertical, so the constraint and its righting torque stay
    // exactly as they were.
    /**
     * |long axis · world up| above which a pose counts as NOSE/TAIL-STANDING and
     * the wreck is refused a rest. 0.35 ≈ 20° of lift — a wreck lying on its
     * side or roof is within a few degrees of horizontal, so this is generous.
     * Also the long-axis half of the topple-complete test (`toppleDoneDot`).
     */
    tipLongDot: 0.35,
    /**
     * |long axis · world up| above which the wreck is judged to be genuinely
     * BALANCED on a short face (0.7 ≈ 45° from horizontal), which arms the
     * nose-grind clock below.
     */
    noseStandDot: 0.7,
    /**
     * Seconds of continuous nose/tail-standing (past `noseStandDot`) after which
     * the controller stops asking politely and FORCES the pitch through about the
     * axis that provably lowers the long axis every step (world.ts
     * `noseRightAxis`). 0.3 s ≈ 18 frames — long enough that a topple passing
     * through a lean is never touched, short enough that no wreck ever grinds
     * along on its front face for a visible distance.
     */
    noseGrindTime: 0.3,
    /** rad/s the forced nose-righting pitch is held at once `noseGrindTime` passes. */
    noseForceRate: 6,
    /**
     * Hard safety cap (s) on ACCUMULATED nose-righting enforcement per wreck, and
     * on the long-axis settle refusal. Its own clock, separate from
     * `toppleMaxTime` and `tipMaxTime`, so the refusals cannot starve each other.
     */
    noseMaxTime: 2,
    /**
     * PER-IMPACT ENERGY HAIRCUT — the fraction of HORIZONTAL speed a wreck keeps
     * through each ground slam.
     *
     * A heavy body arriving on asphalt does not keep its speed through the
     * impact: the crumple, the contact patch and the dig-in eat a chunk of it in
     * one frame. The detector re-arms on each genuine fall (`KICK_SLAM_ARM_VY`),
     * so the touchdown and the topple's landing slam are each charged once.
     * Deterministic, rng-free — a pure function of the integrated velocity.
     *
     * SPIN-OUT PASS: 0.92 -> 0.85. The wreck barely leaves the ground now, so
     * there are far fewer slams to charge; each one has to bite harder for the
     * arrivals to read as mass.
     */
    wreckImpactHaircut: 0.85,
    /**
     * BLINK-OUT DELAY (s) after a wreck's FIRST GROUND IMPACT before the
     * blink-out countdown (`wreckFadeTime`) starts.
     *
     * Owner note (2026-08-03): "Make the crash despawn flashes earlier — right
     * now the crashed vehicle is blocking the view. Start flashing slightly
     * after the vehicle hits the ground." The blink used to be armed by the
     * SETTLE test, i.e. only once the wreck had finished spinning out and
     * toppling (~2-4 s of grinding), so the corridor behind it stayed masked for
     * the whole slide. It is now armed off the touchdown clock
     * (`kickedGroundTime`) instead: the wreck starts flashing while it is still
     * broadsiding down the road, which is the Temple-Run "this is leaving now"
     * language and reads the lane through it immediately.
     *
     * 0.35 s, not 0: the touchdown frame owns the big dust plume, and flashing
     * INSIDE that plume looks like a render bug rather than a despawn cue.
     */
    wreckFadeDelay: 0.35,
    /**
     * BLINK-OUT LINGER (s) — how long a wreck flickers before
     * `kickedVehicleDown` fires and the slot is despawned. Starts at
     * `wreckFadeDelay` after touchdown (NOT at the settle, see above), so the
     * whole flashing window is groundHit + 0.35 s → groundHit + ~2.0 s.
     *
     * The render blinks VISIBILITY at ~9 Hz off the countdown value
     * (VehicleEntity.wreckFade), ending hidden — which requires the countdown to
     * span an EVEN number of 1/18 s intervals, so that the last one is odd
     * (render/vehicleView.ts `wreckBlinkVisible`). 1.0 s was 18 intervals;
     * 30/18 = 1.6667 s is 30, and is long enough that the typical ~0.9 s topple
     * completes while the wreck is already flashing.
     *
     * The pose freeze that used to ride on `wreckFade > 0` now has its own flag
     * (world.ts `kickedFrozen`, set at the settle): the wreck keeps spinning,
     * toppling and settling normally THROUGH the blink, and only the settle
     * freezes it.
     */
    wreckFadeTime: 1.6667,
    /**
     * Angular damping on a kicked wreck (1/s). Friction alone barely touches a
     * box spinning about its own vertical axis on a low-friction surface, so
     * without this a wreck that survives to the rest test can pirouette past
     * KICK_REST_ANG indefinitely and never be reclaimed.
     *
     * TUMBLE PASS: 0.4 -> 0.2. The guaranteed flip (tumble* above) is only worth
     * anything if the rotation SURVIVES the landing — at 0.4 the skid straightened
     * out within a second and the wreck went back to sliding like a plank. At 0.2
     * the wreck keeps turning for the whole visible slide and STILL rests: all 60
     * measured wrecks reached `kickedVehicleDown` (sim/wreckTumble.test.ts), so
     * the rest thresholds (world.ts KICK_REST_ANG) did not need relaxing.
     */
    wreckAngularDamping: 0.2,
    /**
     * Linear damping on a kicked wreck ON THE GROUND (1/s) — ZERO by intent,
     * i.e. once it has landed the wreck's only speed loss is `wreckFriction`.
     * Kept as an explicit constant because it is the natural second lever on
     * slide length and Rapier's default (also 0) is otherwise invisible here.
     *
     * world.ts restores this value on the wreck's first ground impact (the same
     * frame the `kickedVehicleGroundHit` plume fires), replacing the airborne
     * drag below — so the approved long skid is untouched by the drag pass.
     */
    wreckLinearDamping: 0,
    /**
     * AIR DRAG on a kicked wreck while it is still AIRBORNE (1/s) — the
     * "strong air resistance" half of the heavy pass.
     *
     * Set by `applyKick` at launch and swapped back to `wreckLinearDamping` on
     * the first ground impact (world.ts syncKickedBodies). With no drag at all
     * the wreck left at a constant speed, which is what made it read as a
     * weightless prop on rails: a real punted car sheds speed the instant it is
     * off the ground. At 0.8 the wreck loses 1 - e^(-0.8 t) of its launch speed
     * — 13% by the 0.18 s touchdown, and it would asymptote at
     * launchSpeed / 0.8 ≈ 32 m of travel if it never landed — so the departure
     * visibly DECELERATES while still clearing the corridor immediately.
     * Deliberately below 1.0: above that the wreck stalls in mid-air, which
     * reads as slow motion rather than as drag.
     *
     * MASS PASS 2 (2026-08-03): 0.8 -> 0.6. The energy the drag used to take is
     * now taken by `wreckImpactHaircut` — at the SLAM, where the player can see
     * it, instead of silently during a flight that is only 0.15 s long anyway.
     * Trading the two that way lengthens the ground phase (median slide 3.9 m ->
     * 4.6 m) without giving back any of the departure speed the mass passes cut.
     */
    wreckAirDamping: 0.6,
    /**
     * INERT — the corridor-threat exemption for a kicked wreck is PERMANENT.
     *
     * This was the length of a window after the kick during which the wreck
     * ignored driving traffic. Nothing reads it: `applyKick` puts the body in
     * COLLISION_THREAT_KICKED (mask = STATIC only) and nothing ever puts it
     * back, so a wreck ignores driving threats, the player and other wrecks for
     * its whole lifetime — a wreck now SLIDES for seconds along the road, and a
     * sliding wreck that could bounce off (or block, or be shoved by) live
     * traffic would both look wrong and rewrite the spawner's solvable corridor
     * mid-approach. Ground/guardrail contact is unaffected (STATIC is in the
     * mask). Kept as the documented record of the old behaviour.
     */
    airborneIgnoreMs: 600,
    /**
     * Sim freeze on a connecting kick (s). ZERO — the launch is INSTANT.
     *
     * At 0.09 s the kicked vehicle visibly froze for ~90 ms (5 sim steps) with
     * its nose in the player's face before flying: the freeze read as a hitch,
     * not as impact, and it delayed the one thing the player pressed the button
     * for. The plumbing is intentionally left in place (world.ts HITSTOP_STEPS,
     * render/hitstopGate.ts) and degrades to a no-op at 0: HITSTOP_STEPS rounds
     * to 0 frozen steps and the render gate's `trigger()` arms a zero-length
     * window, so no FOV zoom is ever applied. Raise this again and both sides
     * come back without a code change.
     */
    hitstop: 0,
  },

  score: {
    perMeter: 1,
    kickCar: 50,
    kickTruck: 150,
    nearMiss: 25,
    nearMissMargin: 0.5, // m lateral gap counted as near-miss
    multiplierMax: 16,
    multiplierDecayAfter: 5, // s of no kick/near-miss before -1/s decay
    /**
     * Value of ONE coin, added FLAT to `bonusScore` (see sim/coins.ts and
     * scoring.ts `recordCoin`).
     *
     * Deliberately NOT multiplied by the combo multiplier and deliberately NOT
     * a combo action: a chain is 6-10 pickups in under two seconds, so banking
     * it at the live multiplier (and bumping the multiplier eight times) would
     * make the kick/near-miss combo economy meaningless — one chain would pin
     * the multiplier at its cap for free. A chain is worth 60-100, i.e. 3.5-6
     * seconds of distance score at run-start speed: a real reward for taking
     * the risky lane, never the dominant income.
     */
    coin: 10,
  },

  /**
   * COINS — the risk/reward pickup layer (sim/coins.ts, render/coins.ts).
   *
   * A coin is a ROAD-FIXED world object: it closes on the player at exactly the
   * player's own forward speed (vehicles close at playerSpeed + vehicleSpeed).
   * Chains are laid ONLY into a lane that a scheduled vehicle is about to
   * occupy, and the pairing is solved in the TIME domain: the chain's last coin
   * and that vehicle's front reach the player plane `pairLead*` seconds apart,
   * so the sponsor is visibly bearing down as the player takes the last coin.
   * Grabbing the chain means riding a lane that is about to be sealed, and the
   * answer is either dodge out at the last coin or hold and kick.
   *
   * NOTHING here can move the traffic: the chain decision runs on its OWN rng
   * stream (a child of the world seed, never the spawner's), downstream of the
   * spawner, on the commands it has already produced. The 800-seed solvability
   * sweeps in spawner.test.ts are bit-identical with coins on.
   */
  coins: {
    /**
     * Coin pool size.
     *
     * A coin lives from birth (~105 m ahead at run-start speeds, ~133 m at full
     * ramp — see sim/coins.ts for why that distance is fixed by physics) until
     * it is collected or passes the player, i.e. 6.1 s at run start and 3.0 s
     * at full ramp. At the shipped `chainChance` the stream tops out near 70
     * simultaneous live coins (MEASURED, coins.test.ts). 96 leaves ~35% headroom;
     * an exhausted pool is not a bug (the chain is simply dropped and counted,
     * see CoinField.drops) but it is a silent content loss, so size it up rather
     * than let it bind.
     */
    poolSize: 96,
    /** Coins per chain: uniform integer in [chainMin, chainMax]. */
    chainMin: 6,
    chainMax: 10,
    /** Longitudinal spacing between consecutive coins in a chain (m). */
    spacing: 2.5,
    /**
     * Probability that a committed vehicle placement also lays a chain into ITS
     * lane. One draw per spawn command, on the coin rng.
     */
    chainChance: 0.35,
    /**
     * CO-ARRIVAL DELTA (s) — the whole risk rule, and the one number that makes
     * a chain read as "bait in front of that truck" instead of a free-standing
     * dotted line in an empty lane.
     *
     * The chain's LAST coin and its sponsor vehicle's FRONT are placed so they
     * reach the PLAYER PLANE (z = 0) `delta` seconds apart, delta uniform in
     * [pairLeadMin, pairLeadMax]. That is the only frame the pairing can be
     * stated in: a coin is road-fixed and closes at `playerSpeed`, its vehicle
     * closes at `playerSpeed + vehicleSpeed`, so equal ROAD distances are not
     * equal times and vice versa.
     *
     * OWNER BUG (2026-08-02, "some chains are free-standing in an empty lane"):
     * this used to be a lead of 15-25 METRES OF PLAYER TRAVEL. That is 0.87-1.45
     * s at run-start speed, and in that time the vehicle — closing 2.2x faster
     * than the coins — is still 32-54 m behind the last coin when the player
     * gets there. The pairing was real in the schedule and invisible on screen.
     *
     * At [0.35, 0.55] s the sponsor is 13-20 m behind the last coin at run start
     * and 22-35 m at full ramp: unmistakably bearing down, on every model.
     *
     * FLOOR: the player must still be able to leave. The last coin is magneted
     * at `magnetRadius` ahead, i.e. `magnetRadius / playerSpeed` (0.41 s at run
     * start, 0.16 s at full ramp) BEFORE it reaches the plane, so the real
     * escape budget is delta + that: 0.76-0.96 s at run start, 0.51-0.71 s at
     * full ramp, against a ~0.3 s lane change (player.lateralMaxSpeed). Holding
     * the lane and kicking is the other answer, which is the point.
     */
    pairLeadMin: 0.35,
    pairLeadMax: 0.55,
    /**
     * START GRACE (s) — nothing may be COLLECTIBLE before this.
     *
     * OWNER BUG (2026-08-02, "don't have coins immediately at the start"): the
     * spawner emits its whole opening pipeline (~7.3 s of arrivals) on frame
     * ONE, so the earliest of those arrivals used to hang a chain a couple of
     * metres in front of a player who had not moved yet — the run opened with a
     * free pickup.
     *
     * The gate is stated on the COLLECTIBLE instant rather than on the chain's
     * birth: a chain may be committed at any time provided its NEAREST coin
     * cannot enter the magnet window (`magnetRadius` ahead of the player) before
     * `startGrace`. That is exactly the property the owner asked to be able to
     * assert, and unlike a blanket "no chains for the first N seconds" it does
     * not blank the entire opening pipeline — the first coins land right on the
     * boundary instead of ~10 s in.
     *
     * Reset-safe: the gate reads `state.time`, which `startRun` rewinds to 0.
     */
    startGrace: 3,
    /**
     * Fewest coins a chain may consist of. A chain trimmed below this by the
     * per-coin vehicle clamp is not laid at all: two or three coins in a lane
     * read as debris, not as a line the player commits to.
     */
    minChainCoins: 4,
    /**
     * Minimum clear z-gap (m) a new coin needs from any live vehicle in its lane
     * and from any live coin in its lane. The vehicle term is measured to the
     * vehicle's BOX, so this is clearance beyond the bodywork; the coin term
     * keeps two chains from interleaving into one unreadable dotted line.
     */
    minVehicleGap: 6,
    minCoinGap: 10,
    /**
     * MAGNET. A coin is collected the step it is within `magnetRadius` metres
     * AHEAD of the player plane and within `magnetHalfWidth` laterally. The
     * suck is render-only (a screen-space sprite flying to the HUD counter);
     * the sim collects instantly and deterministically.
     *
     * `magnetHalfWidth` (1.6 m) is LANE DISCIPLINE: a lane is 2.7 m wide, so a
     * coin one lane over is 2.7 m away and can never be pulled, however greedy
     * the radius looks.
     */
    magnetRadius: 7,
    magnetHalfWidth: 1.6,
    /** A coin that got past the player is returned to the pool beyond this z. */
    despawnBehind: 5,
    /**
     * Height of a coin's centre above the road (m).
     *
     * LOW ON PURPOSE (owner playtest, 2026-08-02: "can't see past the traffic").
     * At the old 0.9 m a chain sat at rider chest height, directly across the
     * silhouette of the car it was baiting the player into — the pickup hid the
     * threat it was supposed to advertise. At 0.42 m the whole chain lives in
     * the bottom band of the road, under every vehicle's beltline, so the car
     * behind it is fully visible while the coins still read against asphalt.
     *
     * Render-only: the sim's magnet test is 2D (z + lateral), so this number
     * never touches collection. `render/coins.ts` bobs ±BOB_AMPLITUDE about it
     * and `render/juiceCoordinator.ts` starts the flight sprite here.
     */
    height: 0.42,
  },

  view: {
    // Locked: mobile portrait presentation (9:16), letterboxed on desktop.
    aspectW: 9,
    aspectH: 16,
    letterboxColor: "#0b0b10",
  },

  director: {
    // Difficulty ramp progress p = clamp(time / rampDuration)
    rampDuration: 200, // s — matches player.speedRampDuration
    // (The pattern-group levers — patternMinGap / patternGapEarly* /
    // patternUnlock* / patternWeights / truckScreenMinRatio — are GONE with the
    // group spawner they paced. Traffic is a continuous per-lane stream now;
    // every lever that shapes it lives in TUNING.flow below.)
  },

  /**
   * FLOW — the WALL-FIRST traffic generator (spawner.ts).
   *
   * There are no pattern groups and no random per-lane streams. The spawner
   * maintains ONE hidden GUARANTEED PATH — a piecewise-constant lane as a
   * function of ARRIVAL TIME at the player plane — and builds the traffic AROUND
   * it: every time the path steps out of a lane it schedules a BLOCK (1-2
   * vehicles, plus followers) into the lanes the path is NOT in. Nothing is ever
   * generated-then-deleted, so the road cannot go empty; and nothing is ever
   * placed on the path, so the run stays provably survivable.
   *
   * TOMBSTONE — the levers of the old SUBTRACTIVE model are gone:
   *   meanArrivalGap, middleLaneGapScale, maxLaneEmptyTime, maxScreenGap,
   *   sameLaneBurstMax, corridorCloseMin/Max, modeSingleMin/Max,
   *   modeDualMin/Max, dualDensityBoost.
   * They paced three independent random per-lane cursors and the compensator
   * stack (starvation cap, burst cap, global cadence floor, dual-corridor
   * density boost) that tried to repair the holes those cursors left after the
   * path constraint had DELETED whatever landed on the corridor. The wall-first
   * scheduler below has no holes to repair: `blockCloseMin/Max`,
   * `blockFullChance` and the `follower*` levers replace all of them.
   */
  flow: {
    /**
     * Bounds (s) on how long the guaranteed path holds one lane before it
     * steps to an ADJACENT one. Every transition is a lane change the player
     * must make, so this is literally the weave cadence: one forced hop every
     * 0.9-2.0 s. The MEASURED mean hold is ~1.67 s rather than the flat 1.45 s,
     * because pathBounceHoldMin below raises the floor on the excursions that
     * bounce straight back. The floor is what keeps a transition from
     * arriving before the player has finished the previous one (a lane cross is
     * 0.225 s, so 0.9 s is 4x the travel time); the ceiling is what stops the
     * run from ever settling into a lane.
     */
    pathTransitionMin: 0.9, // s
    pathTransitionMax: 2.0, // s
    /**
     * BOUNCE FLOOR (s) — the floor that replaces pathTransitionMin when a
     * corridor's NEXT transition takes it straight back into the lane it just
     * left (which, from an outer lane, is always: the middle lane is the only
     * adjacent one).
     *
     * A lane can only receive traffic while every corridor is out of it for
     * longer than a whole guard window, 2 x (w + crossMargin). Drawn flat from
     * [0.9, 2.0] most bounce excursions were too short for that, and lanes went
     * 10-50 s at a time with nothing in them — the owner's "long stretch of a
     * single lane without traffic". 1.62 s clears one truck guard window at the
     * SLOWEST closing speed of the run (2 x 0.76 s = 1.52 s) plus the spawner's
     * discretization epsilon, so even the widest vehicle fits in the tightest
     * bounce. (The margin grew with the speed pass: every guard window is
     * dominated by crossMargin and shrinks as closing speeds rise, so a floor
     * that cleared it at the old speeds clears it at the new ones too.) Crossing excursions keep the full 0.9 s floor, so the weave still
     * flicks; the measured hold mean rises from 1.45 s to ~1.67 s.
     */
    pathBounceHoldMin: 1.62, // s
    /**
     * Probability that a corridor leaving the MIDDLE lane crosses to the far
     * side (a full-width slalom, 0 -> 1 -> 2) rather than bouncing back the way
     * it came (2 -> 1 -> 2). Crossing leaves the lane just vacated free for
     * three holds (>= 3.4 s) instead of one, so it is the other half of the
     * lane-starvation fix above — and it makes the hidden weave read as a wide
     * sweep rather than a twitch. Not 1.0: the bounce has to stay possible or
     * the corridor becomes a metronome.
     */
    pathAlternateBias: 0.75,

    /**
     * BLOCK CLOSE (s) — how long after the path leaves lane A the block's
     * A-vehicle arrives there.
     *
     * This is the vehicle that seals the corridor the player has just left, and
     * it is why a player can never drift back or idle on a boundary. The delay
     * is uniform in this range, then clamped BELOW by the crossing guard
     * (w + crossMargin, so it can never catch a player still crossing out of A)
     * and ABOVE by the path's next entry into A (spawner.ts emitBlock).
     *
     * 0.32-0.72 s (was 0.6-1.2 in the subtractive model, where it only patched
     * one hole; here it sets the whole road's cadence; 0.4-0.9 before the
     * DENSITY PASS below). The lower clamp usually binds anyway — the truck
     * guard is 0.76 s at run start — and that is the point: an A-vehicle placed
     * at the FIRST legal instant leaves the rest of the lane's narrow legal
     * window free for a follower, which is what pulls the middle lane's mean
     * arrival gap down. Trimming the whole range by 20% on the density pass was
     * what kept the MIDDLE lane improving as fast as the outer ones (its mean
     * gap went 1.75 -> 1.64 s and the outer/middle load ratio 2.07 -> 1.98).
     */
    blockCloseMin: 0.32, // s
    blockCloseMax: 0.72, // s
    /**
     * P(FULL block). At every path transition the spawner draws FULL (a vehicle
     * in the vacated lane A *and* one in the third lane C) or LIGHT (lane A
     * only). C is always an OUTER lane — the path steps to an adjacent lane, so
     * the complement of {A, B} is never the middle one.
     *
     * FULL is the intended texture: a 2-wide wall with exactly one gap, the lane
     * the path just moved into. LIGHT is the breather. Two LIGHT blocks never
     * run BACK TO BACK (spawner.ts `prevLight`): the third lane would then go
     * two whole path holds — up to 4 s — without anything, and measured over 400
     * seeds that tail alone was worth 3 s of per-lane arrival gap. The rule is
     * what makes a high FULL chance safe to push.
     *
     * DENSITY PASS (owner playtest: "still a bit too easy, increase the density
     * of traffic"). 0.8 -> 0.93: LIGHT falls from 16.7% of blocks to 6.5%, so a
     * breather is now rare rather than routine. Measured with the rest of the
     * density pass (followerChance 0.95, followerChainMax 3, followerJitterMax
     * 0.05, blockClose/blockJitter -20%), over 400 ramping + 400 max seeds x
     * 120 s: worst per-lane gap 5.96 s (was 6.16) and mean cross-lane arrival
     * gap 0.335 s ramping / 0.290 s max (was 0.458 / 0.420).
     *
     * Scaled by the density lever (see traffic.densityStart): below densityStart
     * this and `followerChance` are thinned proportionally, so density stays a
     * live volume control. Above it the schedule saturates — there is already
     * one block per transition.
     */
    blockFullChance: 0.93,
    /**
     * Jitter (s, +/-) on the C-vehicle's arrival relative to the A-vehicle's, so
     * a FULL block lands as a shallow ripple instead of a ruled 2-wide line.
     * Unlike a 3-wide kick wall there is no unslippable bound to respect here:
     * the block always leaves the path's lane open, so the player is meant to
     * flow through it.
     *
     * 0.35 -> 0.28 on the density pass. A wide jitter is the one thing that can
     * push a C-vehicle out past the far edge of its lane's legal window, where
     * the legalizer either walks it a whole hold later or skips it; tightening
     * the ripple took the WORST cross-lane arrival gap from 2.46 s back to
     * 2.24 s with no cost to the mean (0.335 s either way).
     */
    blockJitter: 0.28, // s
    /**
     * LEGALIZATION BUDGET (s). A block or follower vehicle whose desired arrival
     * would land on the path's guard window in its own lane is moved FORWARD to
     * the first legal instant. If that instant is more than this far past the
     * desired time the placement is SKIPPED instead — piling it a whole hold
     * later would land it on top of the next block rather than filling the hole
     * it was meant to fill.
     *
     * One full path hold (flow.pathTransitionMax). MEASURED skip rate at this
     * budget: 3.5% of block leaders and 25% of followers (the middle lane's
     * legal window is only ~0.2 s wide at run-start closing speeds, so most of
     * the follower skips are middle-lane followers that simply do not fit).
     */
    blockLegalizeMax: 2.0, // s
    /**
     * P(FOLLOWER) — every scheduled vehicle independently gets a second vehicle
     * behind it in the SAME lane with this probability, arriving one physical
     * headway plus `followerJitterMax` later. Followers may chain
     * (`followerChainMax` deep).
     *
     * Followers are what turn a block into TRAFFIC: without them every lane
     * receives exactly one vehicle per relevant transition, the arrival stream
     * is audibly periodic, and the middle lane — which can only be fed in the
     * narrow window between two path visits — is starved. 0.95 is high on
     * purpose; the same-lane headway floor and the path guard reject the ones
     * that do not fit, so the MEASURED rate is 1.82 emitted followers per
     * emitted leader (19.8% of follower placements are skipped, almost all of
     * them middle-lane ones that genuinely do not fit).
     *
     * DENSITY PASS: 0.85 -> 0.95 together with followerChainMax 2 -> 3. These
     * two are the LARGEST density levers in the file — a queue behind a leader
     * is the only way to add vehicles without touching the path cadence or the
     * guard, i.e. without touching solvability. `requiredHeadway` is still the
     * floor nothing crosses, so pushing them cannot pack a lane tighter than
     * the bike can thread.
     */
    followerChance: 0.95,
    /**
     * Longest chain of followers behind one leader (leader excluded). 2 -> 3 on
     * the density pass: a third link only ever lands where the lane's legal
     * window is genuinely long (the chain stops at the first skip), so it
     * thickens the outer lanes' long excursions without touching the middle
     * lane's narrow ones.
     */
    followerChainMax: 3,
    /**
     * Extra uniform delay (s) on top of a follower's physical headway floor.
     *
     * Deliberately SMALL. The headway floor already guarantees the bike can
     * thread the pair (flow.bikeSpace), so a follower is a tight two-vehicle
     * queue rather than a second event, and every 0.1 s added here is 0.1 s of a
     * legal window spent — which the middle lane, whose window is ~0.2 s wide at
     * run start, does not have. 0.6 -> 0.4 -> 0.15 -> 0.05 across the tuning
     * passes took the middle lane's mean arrival gap from 2.45 s to 1.64 s with
     * no other metric moving. It is deliberately NOT zero: the headway floor
     * varies with the pair of kinds and with the live ramp, but a follower
     * pinned exactly on it would still read as a fixed two-vehicle stamp.
     */
    followerJitterMax: 0.05, // s
    /**
     * CROSSING MARGIN (s) — the solvability core, together with the vehicle's
     * own half-overlap time.
     *
     * A vehicle is dropped if the path occupies its lane anywhere in
     * [arrival - w - c, arrival + w + c], where w is half the time the vehicle
     * takes to pass the bike at its closing speed and c is this margin. c has
     * to cover the player's reaction to a path transition: one lane cross
     * (2.7 / 12 = 0.225 s) plus slack for input latency, lateral inertia (the
     * real player accelerates, the reference bot does not) and the ~0.05 s a
     * vehicle gains on its planned arrival while the speed ramp climbs under
     * it. 0.55 s is 2.4x the bare lane-cross time.
     */
    crossMargin: 0.55, // s
    /**
     * Longitudinal room (m) that must exist between two same-lane vehicles at
     * the moment the trailing one reaches the player — the space the BIKE has
     * to occupy if the player is threading that lane between them. On top of
     * the two half-lengths, so a car behind a semi needs 6 + 2.4 + 5.5 = 13.9 m
     * of arrival separation.
     */
    bikeSpace: 6, // m
    /**
     * Longitudinal clearance (m) required between two same-lane vehicles far
     * from the player (i.e. at spawn). Only guards against interpenetration:
     * a car scheduled just BEFORE a truck in the same lane spawns behind it but
     * closes ~4 m/s faster, so without this the truck would be born on top of
     * the car. No bike ever threads that gap, hence 2 m rather than bikeSpace.
     */
    spawnClearance: 2, // m
    /**
     * ANTI-CAMP (s): the longest an OUTER lane may go without a truck actually
     * landing in it. Only a truck reaches a player parked at the strafe clamp
     * (see road.strafeHalfWidth), so this is what turns "a shoulder camper
     * probably dies" into "a shoulder camper provably dies". The next vehicle
     * scheduled into a starved outer lane is upgraded to a truck, and the timer
     * only resets when a truck is EMITTED there (an upgrade whose placement the
     * legalizer then skips does not count). The car/truck RNG draw happens
     * either way, so the guarantee never shifts the RNG stream.
     */
    outerTruckInterval: 12, // s
    /**
     * WALL EVENTS — the 3-wide forced KICK (spawner.ts header 11).
     *
     * All THREE lanes receive a vehicle at (very nearly) the same arrival time.
     * This is the only permitted violation of the path constraint: the corridor
     * lane gets a vehicle too, so there is no line through it and the ONLY
     * answer is the kick. `firstArrival` is when the first wall may land
     * (arrival-time space); every later one is drawn uniformly
     * `intervalMin..intervalMax` after the previous one actually landed.
     *
     * FREQUENCY (owner playtest: "increase the frequency of 3-wide walls").
     * 25 / 30-55 s gave 2.3-2.5 walls per 120 s run; 15 / 20-38 s gives 3.9-4.0
     * (measured over 400 ramping + 400 max-difficulty seeds), never closer
     * together than 20.04 s.
     * intervalMin MUST stay far above kick.cooldown (5 s) — two walls inside one
     * cooldown would be unanswerable. 20 s is 4x the cooldown, and a test
     * asserts `intervalMin >= 3 * kick.cooldown` (the floor the design allows)
     * as well as the actual 4x. The scheduled time also slides later to sit
     * inside a single stable path segment (the player must be settled on a lane
     * centre when it arrives), and per-lane headway
     * floors can push it later still, so the OBSERVED spacing is only ever
     * larger than intervalMin.
     *
     * STAGGER (`staggerMax`, s). The three vehicles no longer share ONE arrival:
     * each lane draws an independent offset in [0, effectiveStagger] so the wall
     * lands as a shallow ripple instead of a ruler-straight line. It must stay
     * UNSLIPPABLE — see the WALLS section of the spawner.ts header for the
     * derivation. The short version: the three arrival-overlap windows
     * [a_l - w_l, a_l + w_l] must still pairwise overlap for adjacent lanes, and
     * since all offsets live in [0, s] that holds whenever s <= 2 * min_l w_l.
     * w shrinks as the speed ramp climbs (w ~ 0.21 s for a start-speed truck,
     * 0.051 s for a full-ramp car), so the spawner CLAMPS the drawn offsets to
     * that live bound every time a wall is committed — 0.25 is only the ceiling,
     * and the effective spread measures ~0.19 s early and ~0.11 s late in a run.
     *
     * THE ALIGNED PAIR (owner playtest: "vehicles don't appear close enough for
     * a double kick — make 3-wide walls have 2 vehicles aligned"). TWO of the
     * three wall vehicles now share ONE arrival time AND one KIND: a seeded draw
     * per wall picks the adjacent pair (lanes 0+1 or 1+2), and both get the same
     * kind so they close at the same speed and their z gap stays EXACTLY 0 the
     * whole way in — a different kind would drift them apart by metres before the
     * kick moment and out of `kick.pairMaxDz`. A player who straddles that
     * boundary can answer the wall with ONE double kick.
     *
     * `pairSepMin` is the floor (as a fraction of the live stagger span) on the
     * THIRD vehicle's offset from the pair, so the pair is never ambiguous: the
     * wall always reads as "two abreast + one offset", and the aligned pair is
     * the only |dz| ~ 0 couple in it. The third vehicle still lands inside the
     * unslippable span, so nothing about solvability changes.
     */
    wall: {
      // Cadence (owner 2026-08-03: "make the 3-wide wave more frequent"):
      // first wall at 12 s, then one every uniform(15, 22) s — mean ~18.5 s,
      // ~3.2 walls/min (was 15 s + uniform(20, 38), mean ~29 s, ~2/min).
      // intervalMin sits exactly on the DESIGN FLOOR of 3x the kick cooldown
      // (spawner.test.ts pins it): two walls can never land inside one
      // cooldown, with whiff-and-retry margin to spare. Do not go below 15
      // without revisiting that arithmetic.
      firstArrival: 12, // s
      intervalMin: 15, // s
      intervalMax: 22, // s
      staggerMax: 0.25, // s — ceiling; clamped live to 2 * min lane overlap w
      /** Fraction of the live stagger span the THIRD vehicle is offset by, min. */
      pairSepMin: 0.4,
    },
  },
} as const;

export type Tuning = typeof TUNING;
